<template>
  <hr-date-field
    ref="field"
    :model-value="calendarDate"
    :label="label"
    :dense="dense"
    :disable="disable"
    :readonly="readonly"
    :clearable="clearable"
    :options="options"
    :rules="rules"
    lazy-rules
    :legacy-props="{ outlined }"
    :time-zone="currentIANATimezone"
    @update:model-value="onChangeDate"
  >
    <template
      v-if="appendImage"
      #append
    >
      <q-avatar size="24px">
        <img :src="appendImage">
      </q-avatar>
    </template>
  </hr-date-field>
</template>
<script>
import { Timestamp } from 'firebase/firestore';
import { HrDateField } from 'src/design-system';
import { useTime } from 'src/composables/time';

export default {
  name: 'DateInput',

  components: {
    HrDateField,
  },

  props: {
    modelValue: {
      type: [Timestamp, null],
      required: true,
    },
    label: {
      type: String,
      default: null,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Function,
      default: () => (true),
    },
    preserveTime: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: null,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    appendImage: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup() {
    const { getLocaleDateString, extractDate, formatDate, currentIANATimezone } = useTime();
    return { getLocaleDateString, extractDate, formatDate, currentIANATimezone };
  },

  computed: {

    rules() {
      if (this.errorMessage === null) {
        return;
      }
      return [() => (!!this.calendarDate || this.errorMessage)];
    },

    // HrDateField's model is a 'yyyy-MM-dd' calendar string; this component's is a Timestamp, which
    // its callers store directly.
    calendarDate() {
      if (!(this.modelValue instanceof Timestamp)) {
        return null;
      }
      return this.formatDate(this.modelValue.toDate(), 'yyyy-MM-dd');
    }, // calendarDate()
  },

  methods: {
    /**
     * Re-runs the field's rules against the value it now holds.
     *
     * WHY IT HAS TO BE ASKED FOR AT ALL: the field is `lazy-rules`, and Quasar reads that as
     * "validate on blur", never on a value change. That is right for a field the user types into and
     * wrong for this one, whose value only ever arrives from the calendar popup — so an error already
     * on screen survived the user answering it, until they happened to click somewhere that blurred
     * the input. Picking a date IS the interaction lazy-rules is waiting for.
     *
     * Exposed as a method as well, because a rule can compare against a value in another field: the
     * authorization editor's end date has to be re-checked when its start date moves, and that field's
     * own value did not change, so nothing else would re-check it.
     *
     * The wait is for the parent's handler and re-render: this component does not own the value, so
     * the rules would otherwise run against the previous prop.
     */
    async validate() {
      await this.$nextTick();

      return this.$refs.field?.validate() ?? true;
    }, // validate()

    onChangeDate(newCalendarDate) {
      this.$emit('update:modelValue', this.toTimestamp(newCalendarDate));
      this.validate();
    }, // onChangeDate()

    toTimestamp(newCalendarDate) {
      if (newCalendarDate === null) {
        return null;
      }

      if (this.preserveTime && (this.modelValue instanceof Timestamp)) {
        const timeOfDay = this.formatDate(this.modelValue.toDate(), 'HH:mm');
        return Timestamp.fromDate(
          this.extractDate(`${newCalendarDate} ${timeOfDay}`, 'yyyy-MM-dd HH:mm'),
        );
      }

      return Timestamp.fromDate(this.extractDate(newCalendarDate, 'yyyy-MM-dd'));
    }, // toTimestamp()
  },

};
</script>
